<template>
  <div v-loading="loading" class="page-stack">
    <button class="back-link" @click="$router.push('/messages')">
      <i class="el-icon-back"></i> 返回发送记录
    </button>

    <template v-if="detail">
      <section class="detail-hero surface-card">
        <div>
          <p class="eyebrow">MESSAGE TRACE</p>
          <h2 class="mono">{{ detail.message.messageId }}</h2>
          <p>{{ detail.message.bizId }} · {{ detail.message.scene }}</p>
        </div>
        <status-tag :status="detail.message.status" />
      </section>

      <section class="detail-grid">
        <article class="surface-card">
          <div class="section-head"><h3>消息信息</h3></div>
          <dl class="detail-list">
            <div><dt>来源系统</dt><dd>{{ detail.message.sourceSystem }}</dd></div>
            <div><dt>接收人</dt><dd>{{ detail.message.receiverPhone || detail.message.receiverMasked }}</dd></div>
            <div><dt>实际通道</dt><dd>{{ detail.message.actualChannel }}</dd></div>
            <div><dt>短信通道</dt><dd>{{ detail.message.smsChannel || '—' }}</dd></div>
            <div><dt>Sender ID</dt><dd>{{ detail.message.senderId || '—' }}</dd></div>
            <div class="detail-wide"><dt>发送内容</dt><dd>{{ detail.message.renderedContent || '历史记录未留存正文' }}</dd></div>
            <div><dt>最终状态</dt><dd>{{ detail.message.finalStatus || '等待中' }}</dd></div>
            <div><dt>失败码</dt><dd class="mono">{{ detail.message.failureCode || '—' }}</dd></div>
            <div><dt>降级候选</dt><dd>{{ detail.message.fallbackCandidate ? '是（未发送）' : '否' }}</dd></div>
            <div><dt>创建时间</dt><dd>{{ formatDate(detail.message.createdAt) }}</dd></div>
            <div><dt>更新时间</dt><dd>{{ formatDate(detail.message.updatedAt) }}</dd></div>
          </dl>
        </article>

        <article class="surface-card">
          <div class="section-head"><h3>状态时间线</h3></div>
          <div v-if="timeline.length" class="timeline">
            <div v-for="event in timeline" :key="event.key" class="timeline-item">
              <span class="timeline-dot"></span>
              <div>
                <div class="timeline-title">
                  <strong>{{ event.title }}</strong>
                  <status-tag :status="event.status" />
                </div>
                <p>{{ event.description }}</p>
                <small>{{ formatDate(event.time) }}</small>
              </div>
            </div>
          </div>
          <el-empty v-else description="尚未收到供应商事件" :image-size="72" />
        </article>
      </section>

      <section class="surface-card">
        <div class="section-head">
          <div>
            <p class="eyebrow">PROVIDER ATTEMPTS</p>
            <h3>供应商尝试</h3>
          </div>
          <span class="record-count">{{ detail.attempts.length }} 次</span>
        </div>
        <el-table :data="detail.attempts" class="clean-table">
          <el-table-column prop="attemptId" label="Attempt ID" min-width="160" />
          <el-table-column prop="providerCode" label="供应商" min-width="160" />
          <el-table-column prop="senderId" label="Sender ID" min-width="130" />
          <el-table-column label="发送内容" min-width="240" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.renderedContent || '历史记录未留存正文' }}</template>
          </el-table-column>
          <el-table-column prop="providerBatchId" label="Batch ID" min-width="160" />
          <el-table-column prop="providerStrategyId" label="策略" width="100" />
          <el-table-column label="受理" width="120">
            <template slot-scope="{ row }"><status-tag :status="row.acceptStatus" /></template>
          </el-table-column>
          <el-table-column label="投递结果" width="130">
            <template slot-scope="{ row }"><status-tag :status="row.deliveryStatus" /></template>
          </el-table-column>
        </el-table>
      </section>
    </template>
  </div>
</template>

<script>
import { getMessageDetail } from '../api/admin'
import StatusTag from '../components/StatusTag.vue'

export default {
  name: 'MessageDetailView',
  components: { StatusTag },
  data () {
    return {
      loading: true,
      detail: null
    }
  },
  computed: {
    timeline () {
      if (!this.detail) return []
      return (this.detail.timeline || []).map((event, index) => ({
        key: event.eventId || `${event.timelineType}-${event.occurredAt}-${index}`,
        title: this.timelineTitle(event),
        status: event.toStatus || event.providerStatus || event.publicationStatus,
        description: this.timelineDescription(event),
        time: event.occurredAt
      }))
    }
  },
  async created () {
    try {
      this.detail = await getMessageDetail(this.$route.params.messageId)
    } catch (error) {
      this.$message.error('消息详情加载失败')
    } finally {
      this.loading = false
    }
  },
  methods: {
    timelineTitle (event) {
      const titles = {
        request_accepted: '网关已受理',
        provider_dispatch_claimed: '供应商发送中',
        provider_accepted: '供应商已受理',
        provider_outcome_unknown: '供应商结果待确认',
        provider_call_connected: '语音已接通',
        provider_call_final_failed: '语音最终未接通',
        callback_timeout: '等待回调超时',
        provider_late_timeout_corrected: '晚到回调已纠偏',
        upstream_cancelled: '上游已取消',
        provider_voice_result: '供应商语音事件',
        upstream_cancellation: '取消请求',
        'message.status.changed': '状态事件发布'
      }
      return titles[event.eventType] || event.eventType || '状态事件'
    },
    timelineDescription (event) {
      if (event.timelineType === 'voice') {
        return `第 ${event.roundNumber || '—'} 轮 · 振铃 ${event.ringTimeSeconds || 0}s · 通话 ${event.talkDurationSeconds || 0}s`
      }
      if (event.timelineType === 'outbox') {
        return `事件 ${event.eventId} · ${event.publicationStatus}${event.publicationErrorCode ? ` · ${event.publicationErrorCode}` : ''}`
      }
      if (event.timelineType === 'cancellation') {
        return `本地 ${event.toStatus || '—'} · 供应商 ${event.providerStatus || '—'}`
      }
      const transition = `${event.fromStatus || '开始'} → ${event.toStatus || '—'}${event.reasonCode ? ` · ${event.reasonCode}` : ''}`
      const providerCode = event.providerResponseCode ? ` · 厂商 code=${event.providerResponseCode}` : ''
      const providerMessage = event.providerResponseMessage ? ` · ${event.providerResponseMessage}` : ''
      return `${transition}${providerCode}${providerMessage}`
    },
    formatDate (value) {
      return value ? value.replace('T', ' ') : '—'
    }
  }
}
</script>
